import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import axios from "axios";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import SignupSuccess from "./pages/SignupSuccess";
import SignupCancel from "./pages/SignupCancel";
import RequestReset from "./pages/RequestReset";
import ResetPassword from "./pages/ResetPassword";
import ImportConfirmation from "./pages/ImportConfirmation";
import ApproveEmail from "./pages/ApproveEmail";
import ProtectedRoute from "./components/ProtectedRoute";
import DashboardLayout from "./components/DashboardLayout";
import DashboardHome from "./pages/DashboardHome";
import Profile from "./pages/Profile";
import Horses from "./pages/horses/HorseList";
import HorseInfo from "./pages/horses/HorseInfo";
import HorsesVaccinations from "./pages/horses/HorsesVaccinations";
import HorsesBreeding from "./pages/horses/HorsesBreedings";
import HorsesFarrier from "./pages/horses/HorsesFarrier";
import HorsesDentalCare from "./pages/horses/HorsesDentalCare";
import HorsesDeworming from "./pages/horses/HorsesDeworming";
import HorsesMedicalHistory from "./pages/horses/HorsesMedicalHistory";
import HorsesFeedPlan from "./pages/horses/HorsesFeedPlan";
import HorsesFeedProducts from "./pages/horses/HorsesFeedProducts";
import HorsesFeedReport from "./pages/horses/HorsesFeedReport";
import Calendar from "./pages/Calendar";
import Connect from "./pages/connect/Connect";
import Results from "./pages/results/Results";
import Entries from "./pages/Entries";
import LiveResults from "./pages/LiveResults";
import Memberships from "./pages/associations/Memberships";
import Veterinarians from "./pages/Veterinarians";
import Farriers from "./pages/Farriers";
import Media from "./pages/Media";
import Settings from "./pages/settings/Settings";
import Subscription from "./pages/Subscription";
import FamilyManagement from "./pages/FamilyManagement";
import Analytics from "./pages/Analytics";
import Supplies from "./pages/Supplies";
import SuppliesOrderDetails from "./pages/SuppliesOrderDetails";
import SuppliesLookups from "./pages/SuppliesLookups";
import SuppliesReports from "./pages/SuppliesReports";
import ResultsReports from "./pages/ResultsReports";
import SavedReports from "./pages/SavedReports";
import SavedReportView from "./pages/SavedReportView";
import ScoreSimulator from "./pages/ScoreSimulator";
import Help from "./pages/Help";
import LiveStream from "./pages/streaming/LiveStream";
import FanView from "./pages/streaming/FanView";
import { NotificationProvider } from "./contexts/NotificationContext";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { GlobalLoadingProvider } from "./contexts/GlobalLoadingContext";
import { iframeEntryService } from "./services/iframeEntryService";
import Privacy from "./pages/Privacy";
import LoadingScreen from "./components/LoadingScreen";
import UpdateBanner from "./components/UpdateBanner";

import { isAuthenticated } from "./utils/auth";

// Set global axios default header for API key
axios.defaults.headers.common['x-api-key'] = process.env.REACT_APP_API_KEY;

// Set up axios interceptor to automatically include Authorization header
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Initialize Stripe
const stripePromise = (() => {
  const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (!publishableKey) {
    console.error("=== STRIPE CONFIGURATION ERROR ===");
    console.error("Stripe publishable key not found!");
    console.error("Expected: REACT_APP_STRIPE_PUBLISHABLE_KEY");
    console.error("Current value:", process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
    console.error("Environment:", process.env.NODE_ENV);
    console.error("App Environment:", process.env.REACT_APP_ENVIRONMENT);
    console.error("All env vars:", Object.keys(process.env).filter(key => key.includes('STRIPE')));
    console.error("================================");
    return null;
  }
  // Stripe configured successfully
  return loadStripe(publishableKey);
})();

export default function App() {
  const [isLoading, setIsLoading] = useState(true);
  const authenticated = isAuthenticated();

  // Handle initial app loading
  useEffect(() => {
    // Simulate app initialization time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500); // Show loading for 1.5 seconds

    return () => clearTimeout(timer);
  }, []);

  // Suppress ResizeObserver loop error globally
  React.useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver loop')) {
        return;
      }
      originalError.apply(console, args);
    };

    return () => {
      console.error = originalError;
    };
  }, []);

  // Start listening for iframe entry messages when authenticated
  React.useEffect(() => {
    if (authenticated) {
      // Start listening for iframe messages
      iframeEntryService.startListening();
      
      // Listen for entry processing events to show notifications
      const handleEntryProcessed = (event) => {
        const { type, message } = event.detail;
        
        // Dispatch to notification system
        const notificationEvent = new CustomEvent('show-notification', {
          detail: { type, message }
        });
        window.dispatchEvent(notificationEvent);
      };
      
      window.addEventListener('entry-processed', handleEntryProcessed);
      
      return () => {
        iframeEntryService.stopListening();
        window.removeEventListener('entry-processed', handleEntryProcessed);
      };
    }
  }, [authenticated]);

  // Show loading screen during initial app load
  if (isLoading && authenticated) {
    return <LoadingScreen />;
  }

  return (
    <Elements stripe={stripePromise}>
      <GlobalLoadingProvider>
        <NotificationProvider>
          <WebSocketProvider>
            <SettingsProvider>
              <Router>
                <UpdateBanner />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/signup-success" element={<SignupSuccess />} />
            <Route path="/signup-cancel" element={<SignupCancel />} />
            <Route path="/reset-request" element={<RequestReset />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/import-confirm/:token" element={<ImportConfirmation />} />
            <Route path="/approve-email/:token" element={<ApproveEmail />} />
            <Route path="/privacy" element={<Privacy />} />
            
              <Route
                path="/"
                element={
                  authenticated ? (
                    <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                ) : (
                  <Navigate to="/login" />
                )
              }
            >
            <Route index element={<DashboardHome />} />
            <Route path="profile" element={<Profile />} />
            <Route path="horses" element={<Horses />} />
            <Route path="horses/:id/info" element={<HorseInfo />} />
            <Route path="horses/:id/vaccinations" element={<HorsesVaccinations />} />
            <Route path="horses/:id/breedings" element={<HorsesBreeding />} />
            <Route path="horses/:id/farrier" element={<HorsesFarrier />} />
            <Route path="horses/:id/dentalcare" element={<HorsesDentalCare />} />
            <Route path="horses/:id/deworming" element={<HorsesDeworming />} />
            <Route path="horses/:id/medicalhistory" element={<HorsesMedicalHistory />} />
            <Route path="horses/:id/feedplan" element={<HorsesFeedPlan />} />
            <Route path="horses/feedplan" element={<HorsesFeedPlan />} />
            <Route path="horses/feed-products" element={<HorsesFeedProducts />} />
            <Route path="horses/feed-report" element={<HorsesFeedReport />} />
            <Route path="calendar" element={<Calendar />} />
            <Route path="connect" element={<Connect />} />
            <Route path="results" element={<Results />} />
            <Route path="entries" element={<Entries />} />
            <Route path="liveresults" element={<LiveResults />} />
            <Route path="memberships" element={<Memberships />} />
            <Route path="veterinarians" element={<Veterinarians />} />
            <Route path="help" element={<Help />} />
            <Route path="farriers" element={<Farriers />} />
            <Route path="media" element={<Media />} />
            <Route path="supplies" element={<Supplies />} />
            <Route path="supplies/:id" element={<SuppliesOrderDetails />} />
            <Route path="supplies-lookups" element={<SuppliesLookups />} />
            <Route path="supplies/reports" element={<SuppliesReports />} />
            <Route path="results/reports" element={<ResultsReports />} />
            <Route path="insights" element={<SavedReports />} />
            <Route path="insights/:id" element={<SavedReportView />} />
            <Route path="insights/:id/edit" element={<SavedReportView />} />
            <Route path="score-simulator" element={<ScoreSimulator />} />
            <Route path="settings" element={<Settings />} />
            <Route path="subscription" element={<Subscription />} />
            <Route path="family" element={<FamilyManagement />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="streaming/live" element={<LiveStream />} />
            <Route path="streaming/fan-view" element={<FanView />} />

          </Route>
                 {/* Catch-all to redirect unknown routes */}
          <Route path="*" element={<Navigate to="/login" replace />} />
                  </Routes>
              </Router>
            </SettingsProvider>
          </WebSocketProvider>
        </NotificationProvider>
      </GlobalLoadingProvider>
    </Elements>
  );
}
